import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getCertificateVerifications } from "@/api/client";
import { formatDate } from "@/components/format";
import { ScrollArea } from "@/components/ScrollArea";
import { EmptyState, ErrorBanner, StatusBadge, ToolbarButton } from "@/components/ui";

type CopyState = "idle" | "copied" | "manual";

export function VerificationPanel({ certificateId }: { certificateId: string }) {
  const headingId = useId();
  const [copy, setCopy] = useState<CopyState>("idle");
  const query = useQuery({
    queryKey: ["certificate-verifications", certificateId],
    queryFn: () => getCertificateVerifications(certificateId),
  });
  const data = query.data;

  async function copyLink() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.url);
      setCopy("copied");
    } catch {
      // Clipboard missing or denied: expose the URL for manual copying.
      setCopy("manual");
    }
  }

  const missing = query.error instanceof ApiError && query.error.status === 404;
  let body: React.ReactNode = null;
  if (data) {
    body = (
      <div className="space-y-3 text-sm">
        <p className="font-mono text-xs text-zinc-800">
          {data.code ? `Verification code: ${data.code}` : "No code (issued before verification codes)"}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <ToolbarButton onClick={copyLink}>Copy verification link</ToolbarButton>
          <span role="status" className="text-xs text-emerald-700">
            {copy === "copied" ? "Link copied." : ""}
          </span>
        </div>
        {copy === "manual" ? (
          <div className="space-y-1">
            <input
              readOnly
              aria-label="Verification link"
              value={data.url}
              onFocus={(e) => e.currentTarget.select()}
              autoFocus
              className="w-full rounded-md border border-zinc-300 px-3 py-2 font-mono text-xs"
            />
            <p className="text-xs text-zinc-500">Copy it from the box.</p>
          </div>
        ) : null}
        <p className="text-zinc-600">
          {data.count > 0
            ? `Checked ${data.count} ${data.count === 1 ? "time" : "times"} · last on ${formatDate(data.last_at ?? undefined)}`
            : "Not checked yet"}
        </p>
        {data.items.length > 0 ? (
          <ScrollArea label="Recent verification checks">
            <table aria-label="Recent verification checks" className="w-full text-left text-xs">
              <thead className="text-zinc-500">
                <tr>
                  <th className="py-1 pr-3 font-medium">Time</th>
                  <th className="py-1 pr-3 font-medium">Result</th>
                  <th className="py-1 pr-3 font-medium">How</th>
                  <th className="py-1 font-medium">Client</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, i) => (
                  <tr key={`${item.at}-${i}`} className="border-t border-zinc-100">
                    <td className="py-2 pr-3">{formatDate(item.at)}</td>
                    <td className="py-2 pr-3">
                      <StatusBadge status={item.result} />
                    </td>
                    <td className="py-2 pr-3">{item.via === "qr" ? "QR" : "Code"}</td>
                    <td className="py-2 font-mono">{item.client}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        ) : null}
        <p className="text-xs text-zinc-500">Checks older than a year are removed.</p>
      </div>
    );
  } else if (missing) {
    body = <p className="text-sm text-zinc-500">No verification record for this certificate.</p>;
  } else if (query.isLoading) {
    body = <EmptyState compact title="Loading verification" detail="Fetching checks." />;
  }

  return (
    <section aria-labelledby={headingId} className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id={headingId} className="text-sm font-semibold">
        Verification
      </h2>
      {missing ? null : <ErrorBanner error={query.error} />}
      {body}
    </section>
  );
}
